(() => {
  if (customElements.get('mobilegs-pdp-viewer')) return;
  const scriptUrl = document.currentScript?.src || document.baseURI;
  const modules = new Map();
  // Same bounded failed-only import retry as Studio v24; no renderer is loaded
  // here until the owner of a real gallery requests activation.
  const loadModule = source => {
    const url = new URL(source, scriptUrl);
    let entry = modules.get(url.href);
    if (!entry) { entry = { promise: null, failures: 0 }; modules.set(url.href, entry); }
    if (!entry.promise) {
      if (entry.failures) url.searchParams.set('mgss_retry', String(entry.failures));
      const pending = import(url.href);
      entry.promise = pending;
      pending.catch(() => { if (entry.promise === pending) { entry.promise = null; entry.failures += 1; } });
    }
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('The 3D viewer script took too long to load.')), 20000);
      entry.promise.then(value => { clearTimeout(timer); resolve(value); }, error => { clearTimeout(timer); reject(error); });
    });
  };

  class MobilegsPdpViewer extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      if (!customElements.get('media-gallery')) {
        customElements.whenDefined('media-gallery').then(() => { if (this.isConnected) this.connectedCallback(); });
        return;
      }
      this.gallery = this.closest('media-gallery');
      this.section = this.closest('[id^="MainProduct-"]');
      this.picker = this.section?.querySelector('variant-radios, variant-selects');
      this.form = document.getElementById(`product-form-${this.dataset.sectionId}`);
      if (!this.gallery || !this.section || !this.form) return;
      try { this.mappings = JSON.parse(this.querySelector('[data-pdp-mappings]').textContent); }
      catch { return; }
      this.initialized = true;
      this.dead = false;
      this.generation = 0;
      this.cleanups = [];
      this.frame = null;
      this.covered = null;
      this.controller = null;
      this.activation = null;
      this.abort = null;
      this.active = false;
      this.wants3D = false;
      this.stage = this.querySelector('[data-pdp-stage]');
      this.region = this.querySelector('[data-pdp-region]');
      this.activateButton = this.querySelector('[data-pdp-activate]');
      this.activateLabel = this.querySelector('[data-pdp-activate-label]');
      this.backButton = this.querySelector('[data-pdp-back]');
      this.status = this.querySelector('[data-pdp-status]');
      this.loading = this.querySelector('[data-pdp-loading]');
      this._mobilegsCueController = window.MobilegsViewerCue?.pdp(this);
      if (this._mobilegsCueController) this.cleanups.push(() => { this._mobilegsCueController.destroy(); this._mobilegsCueController = null; });
      this.gallery.classList.add('mg-pdp-gallery');
      this.setupPresentation();
      this.setupUniversalDesigns();
      const dismissHint = event => {
        if (this.active && event.composedPath().some(node => node.localName === 'model-viewer')) this.classList.add('has-viewer-interacted');
      };
      ['pointerdown', 'wheel', 'keydown'].forEach(type => this.listen(this.region, type, dismissHint, { passive: true }));
      this.listen(this.activateButton, 'click', event => this.activate(event.detail === 0));
      this.listen(this.backButton, 'click', () => this.photos({ focus: true, restoreVariant: true }));
      this.listen(this.section, 'change', event => {
        if (event.target === this.form.querySelector('[name="id"]')) this.syncVariant();
      });
      this.listen(this.gallery, 'click', event => {
        const thumbnail = event.target.closest('[id^="GalleryThumbnails"] button');
        if (thumbnail || event.target.closest('.slider-buttons button')) {
          this.photos();
          // Gallery thumbnail handlers bind the original setActiveMedia in
          // their constructor, before this adapter can wrap it. Follow their
          // explicit target; the native handler still owns photo/aria updates.
          const mediaId = thumbnail?.closest('[data-target]')?.dataset.target;
          if (mediaId) this.slide = this.gallery.querySelector(`[data-media-id="${CSS.escape(mediaId)}"]`) || this.slide;
          this.scheduleMeasure();
        }
      }, true);
      const originalMedia = this.gallery.setActiveMedia;
      if (originalMedia) {
        const mount = this;
        this.gallery.setActiveMedia = function(mediaId, prepend) {
          // Dawn requests prepend only for variant changes. Keep those on the
          // dedicated still; explicit photo navigation retains its native ID.
          if (prepend && mount.posterSlide && mount.mappings[mount.picker?.currentVariant?.id]?.poster) {
            mount.syncOriginalPhoto(mount.mappings[mount.picker.currentVariant.id]);
            mediaId = mount.posterSlide.dataset.mediaId;
          }
          const result = originalMedia.call(this, mediaId, prepend);
          mount.slide = this.querySelector(`[data-media-id="${CSS.escape(mediaId)}"]`);
          mount.scheduleMeasure();
          return result;
        };
        this.cleanups.push(() => { this.gallery.setActiveMedia = originalMedia; });
      }
      this.listen(this.gallery.querySelector('[id^="GalleryViewer-"]'), 'slideChanged', event => {
        if (!event.target.id.startsWith('GalleryViewer-')) return;
        const next = event.detail?.currentElement;
        if (!next) return;
        // Native variant changes also move the photo slider. Keep 3D when that
        // move points at the selected variant; ordinary photo navigation exits.
        if (this.wants3D && next.dataset.mediaId !== this.variantMediaId()) this.photos();
        this.slide = next;
        this.scheduleMeasure();
      });
      const slider = this.gallery.querySelector('[id^="Slider-Gallery-"]');
      // Native snap can leave WebKit between slides after a partial scroll.
      // Finish only after scrolling AND the user's gesture have ended.
      const pointers = new Set();
      let touching = false, snapTimer;
      const settleGallery = () => {
        clearTimeout(snapTimer);
        if (pointers.size || touching) return;
        snapTimer = setTimeout(() => this.snapGallery(slider), 160);
      };
      this.listen(slider, 'pointerdown', event => { pointers.add(event.pointerId); clearTimeout(snapTimer); }, { passive: true });
      ['pointerup', 'pointercancel'].forEach(type => this.listen(window, type, event => { if (pointers.delete(event.pointerId)) settleGallery(); }, { passive: true }));
      this.listen(slider, 'touchstart', () => { touching = true; clearTimeout(snapTimer); }, { passive: true });
      ['touchend', 'touchcancel'].forEach(type => this.listen(window, type, event => { if (touching) { touching = event.touches.length > 0; settleGallery(); } }, { passive: true }));
      this.listen(slider, 'scroll', () => { this.scheduleMeasure(); settleGallery(); }, { passive: true });
      this.listen(slider, 'scrollend', settleGallery, { passive: true });
      this.cleanups.push(() => clearTimeout(snapTimer));
      this.listen(window, 'resize', () => this.scheduleMeasure(), { passive: true });
      this.listen(document, 'fullscreenchange', () => this.scheduleMeasure());
      this.listen(window, 'popstate', () => this.syncHistory());
      this.listen(window, 'pageshow', () => { this.syncHistory(); this.scheduleMeasure(); });
      this.listen(document, 'shopify:section:unload', event => {
        if (event.target.contains(this)) this.destroy();
      });
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cleanups.push(subscribe(PUB_SUB_EVENTS.variantChange, event => {
          if (String(event.data.sectionId) === this.dataset.sectionId) this.syncVariant();
        }));
      }
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.scheduleMeasure());
        this.resizeObserver.observe(this.gallery);
        this.gallery.querySelectorAll('.product-media-container').forEach(frame => this.resizeObserver.observe(frame));
      }
      this.gallery.querySelectorAll('img').forEach(img => {
        const old = img.getAttribute('draggable');
        img.setAttribute('draggable', 'false');
        this.cleanups.push(() => old === null ? img.removeAttribute('draggable') : img.setAttribute('draggable', old));
      });
      this.syncVariant();
      this.scheduleMeasure();
      this.dataset.pdpReady = 'true';
    }


    // Universal uses the same presentation classes and gallery as Ultra.
    // Published catalog variants use the native Shopify radio/price/cart flow.
    // Unpublished designs retain their review-only state until launch.
    setupUniversalDesigns() {
      if (this.dataset.universalDesigns !== 'true') return;
      try { this.designs = JSON.parse(this.querySelector('[data-universal-designs]').textContent); }
      catch { return; }
      if (!this.designs?.length) return;
      this.designs.forEach(design => {
        design.variant = Object.values(this.mappings).find(mapping => design.classic ? mapping.classic : mapping.name === design.name);
        if (design.variant) Object.assign(design.variant, { poster: design.poster, shortTexture: design.shortTexture, longTexture: design.longTexture });
      });
      this.universalCommerce = !!this.picker && this.designs.every(design => design.variant);
      this.dataset.universalCommerce = String(this.universalCommerce);
      const initialId = this.form.querySelector('[name="id"]').value;
      this.previewDesign = this.designs.find(design => String(design.variant?.id) === initialId) || this.designs[0];
      const root = document.createElement('section');
      root.className = 'mg-pdp-selector mg-universal-designs';
      root.setAttribute('aria-label', 'Choose your Mobilegs style');
      root.innerHTML = '<div class="mg-pdp-selector__heading"><h2>Choose your style</h2><span class="mg-pdp-selector__count"></span></div><div class="mg-pdp-selector__choices" role="group" aria-label="Design options"></div><p class="mg-pdp-selector__selected" aria-live="polite"><strong></strong><span></span></p><p class="mg-pdp-selector__notice">Designs are preview only until launch.</p>';
      root.querySelector('.mg-pdp-selector__count').textContent = this.designs.length + ' options';
      this.designSelector = root;
      // Until real design variants are launched, never submit the base variant
      // while displaying a designed model and its approved preview price.
      this.listen(this.form, 'submit', event => {
        if (!this.universalCommerce && !this.previewDesign?.classic) { event.preventDefault(); event.stopImmediatePropagation(); }
      }, true);
      this.cleanups.push(() => this.restoreUniversalPurchase());
      const options = root.querySelector('.mg-pdp-selector__choices');
      this.designs.forEach((design, index) => {
        const button = document.createElement('button');
        button.type = 'button'; button.dataset.design = design.slug;
        button.className = 'mg-pdp-selector__option';
        if (design.classic) button.dataset.key = 'classic_white';
        button.setAttribute('aria-label', design.name); button.title = design.name;
        if (this.universalCommerce && !design.variant.available) {
          button.title += ' — Sold out';
          const stock = document.createElement('span'); stock.className = 'mg-pdp-selector__stock'; stock.textContent = 'Sold out';
          button.append(stock);
        }
        const swatch = document.createElement('span'); swatch.className = 'mg-pdp-selector__swatch';
        const image = document.createElement('img');
        image.src = design.chip; image.alt = ''; image.loading = 'lazy'; image.draggable = false;
        swatch.append(image);
        const label = document.createElement('span'); label.className = 'mg-pdp-selector__name'; label.textContent = design.name;
        button.append(swatch, label); options.append(button);
        this.listen(button, 'click', () => {
          if (this.universalCommerce) {
            const input = [...this.picker.querySelectorAll('input[type="radio"]')].find(input => input.value === design.variant.name);
            if (!input) return;
            input.click();
            this.gallery.setActiveMedia(this.variantMediaId(), false);
            this.scheduleMeasure();
            return;
          }
          this.previewDesign = design;
          this.updatePresentation();
          this.gallery.setActiveMedia(this.variantMediaId(), false);
          if (this.controller && this.wants3D) this.applySelection();
          this.scheduleMeasure();
        });
        this.listen(button, 'keydown', event => {
          const step = ['ArrowRight', 'ArrowDown'].includes(event.key) ? 1 : ['ArrowLeft', 'ArrowUp'].includes(event.key) ? -1 : 0;
          if (!step) return;
          event.preventDefault();
          const next = options.children[(index + step + this.designs.length) % this.designs.length]; next.focus(); next.click();
        });
      });
      const info = this.section.querySelector('product-info');
      const media = this.section.querySelector('.product__media-wrapper');
      const title = info.querySelector('.product__title');
      const subtitle = title?.nextElementSibling?.matches('.product__text') ? title.nextElementSibling : null;
      const rating = info.querySelector('.product__rating-link');
      const quantity = info.querySelector('.product-form__quantity');
      const form = info.querySelector('product-form.product-form');
      const anchors = new Map([title, subtitle, rating, quantity, form].filter(Boolean).map(node => {
        const anchor = document.createComment('Original Universal product position'); node.before(anchor); return [node, anchor];
      }));
      const purchase = document.createElement('div');
      anchors.get(quantity).after(purchase); purchase.append(quantity, form);
      const mobile = matchMedia('(width < 750px)');
      let heading = media.querySelector('[data-pdp-initial-heading]');
      const position = () => {
        if (this.dead) return;
        if (mobile.matches) {
          if (!heading) { heading = document.createElement('div'); heading.className = 'mg-style-studio__hero-info'; }
          heading.replaceChildren(...[title, subtitle, rating].filter(Boolean));
          title.classList.add('mg-style-studio__gallery-title');
          subtitle?.classList.add('mg-style-studio__subtitle'); rating?.classList.add('mg-style-studio__rating');
          this.gallery.before(heading); media.append(root);
          purchase.className = 'mg-style-studio__purchase-row';
          info.classList.add('mg-style-studio__mobile-purchase-info');
        } else {
          for (const node of [title, subtitle, rating].filter(Boolean)) anchors.get(node).after(node);
          title.classList.remove('mg-style-studio__gallery-title');
          subtitle?.classList.remove('mg-style-studio__subtitle'); rating?.classList.remove('mg-style-studio__rating');
          heading?.remove(); heading = null;
          purchase.before(root); purchase.className = 'mg-pdp-compact-purchase';
          info.classList.remove('mg-style-studio__mobile-purchase-info');
        }
        this.scheduleMeasure();
      };
      this.listen(mobile, 'change', position);
      document.documentElement.classList.add('mg-pdp-layout-ready', 'mg-pdp-compact', 'mg-style-studio-active');
      position();
      this.cleanups.push(() => {
        for (const [node, anchor] of anchors) { anchor.replaceWith(node); }
        root.remove(); heading?.remove(); purchase.remove();
        document.documentElement.classList.remove('mg-pdp-layout-ready', 'mg-pdp-compact', 'mg-style-studio-active');
      });
      this.setupPoster();
    }

    setupPresentation() {
      const fields = [...(this.picker?.querySelectorAll('fieldset') || [])];
      const nativeStudio = this.section.querySelector('[data-mg-style-studio]');
      const variants = this.picker?.getVariantData?.();
      if (fields.length !== 1 || !nativeStudio || !variants?.length) return;
      const inputs = [...fields[0].querySelectorAll('input[type="radio"]')];
      const labels = [...nativeStudio.querySelectorAll('[data-mg-style-option]')];
      const entries = variants.map(variant => ({
        variant, mapping: this.mappings[variant.id],
        input: inputs.find(input => input.value === variant.option1),
        label: labels.find(label => label.htmlFor === inputs.find(input => input.value === variant.option1)?.id)
      }));
      if (entries.some(entry => !entry.mapping || !entry.input || !entry.label)) return;
      const root = document.createElement('section');
      root.className = 'mg-pdp-selector';
      root.setAttribute('aria-label', 'Choose your Mobilegs style');
      root.innerHTML = '<div class="mg-pdp-selector__heading"><h2>Choose your style</h2><span class="mg-pdp-selector__count"></span></div><div class="mg-pdp-selector__choices" role="group" aria-label="Design options"></div><p class="mg-pdp-selector__selected" aria-live="polite"><strong></strong><span></span></p>';
      root.querySelector('.mg-pdp-selector__count').textContent = entries.length + ' options';
      this.presentationEntries = entries;
      this.selector = root;
      entries.forEach(entry => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'mg-pdp-selector__option';
        button.dataset.variantId = String(entry.variant.id);
        if (entry.mapping.classic) button.dataset.key = 'classic_white';
        button.title = entry.mapping.name + (entry.variant.available ? '' : ' — Sold out');
        const swatch = document.createElement('span');
        swatch.className = 'mg-pdp-selector__swatch';
        const image = document.createElement('img');
        image.src = entry.mapping.chip || entry.label.querySelector('img')?.src || '';
        image.alt = '';
        image.loading = 'lazy';
        image.draggable = false;
        swatch.append(image);
        const name = document.createElement('span');
        name.className = 'mg-pdp-selector__name';
        name.textContent = entry.mapping.name;
        button.append(swatch, name);
        if (!entry.variant.available) {
          const stock = document.createElement('span');
          stock.className = 'mg-pdp-selector__stock';
          stock.textContent = 'Sold out';
          button.append(stock);
        }
        this.listen(button, 'click', () => entry.input.click());
        this.listen(button, 'keydown', event => {
          const step = ['ArrowRight', 'ArrowDown'].includes(event.key) ? 1 : ['ArrowLeft', 'ArrowUp'].includes(event.key) ? -1 : 0;
          if (!step) return;
          event.preventDefault();
          const next = entries[(entries.indexOf(entry) + step + entries.length) % entries.length];
          next.button.focus();
          next.input.click();
        });
        entry.button = button;
        root.querySelector('.mg-pdp-selector__choices').append(button);
      });
      const mediaWrapper = this.section.querySelector('.product__media-wrapper');
      const mobile = matchMedia('(width < 750px)');
      let purchaseRow;
      const position = () => {
        if (this.dead) return;
        const destination = mobile.matches ? mediaWrapper : this.picker;
        if (root.parentElement !== destination) destination.append(root);
        if (!mobile.matches && !purchaseRow) {
          const quantity = this.section.querySelector('.product-form__quantity');
          const form = this.section.querySelector('product-form.product-form');
          if (quantity && form) {
            purchaseRow = document.createElement('div');
            purchaseRow.className = 'mg-pdp-compact-purchase';
            quantity.before(purchaseRow);
            purchaseRow.append(quantity, form);
          }
        }
        this.scheduleMeasure();
      };
      // Native mobile reparenting runs first. Re-check after fractional zoom,
      // bfcache restore and rotation using the very same media query as CSS.
      const schedulePosition = () => requestAnimationFrame(position);
      this.listen(mobile, 'change', schedulePosition);
      this.listen(window, 'resize', schedulePosition, { passive: true });
      this.listen(window, 'pageshow', schedulePosition);
      position();
      document.documentElement.classList.add('mg-pdp-layout-ready', 'mg-pdp-compact');
      this.cleanups.push(() => {
        root.remove();
        if (purchaseRow?.isConnected) { purchaseRow.before(...purchaseRow.childNodes); purchaseRow.remove(); }
        document.documentElement.classList.remove('mg-pdp-layout-ready', 'mg-pdp-compact');
      });
      this.setupPoster();
    }
    setupPoster() {
      // The server-rendered image links reserve the exact thumbnail grid and
      // remain useful without JS. Replace them in the same task as native slides.
      this.gallery.querySelectorAll('[data-pdp-initial-thumbnail]').forEach(node => node.remove());
      this.addGalleryPhoto({ key: 'presentation', poster: true });
      let details = [];
      try { details = JSON.parse(this.querySelector('[data-pdp-detail-photos]')?.textContent || '[]'); }
      catch { return; }
      details.filter(detail => detail.src && detail.key).forEach(detail => this.addGalleryPhoto(detail));
    }
    addGalleryPhoto({ key, src, alt, poster = false }) {
      const list = this.gallery.querySelector('[id^="Slider-Gallery-"]');
      const thumbs = this.gallery.querySelector('[id^="Slider-Thumbnails-"]');
      const modal = document.getElementById('ProductModal-' + this.dataset.sectionId);
      const modalList = modal?.querySelector('.product-media-modal__content');
      const photo = [...(list?.children || [])].find(item => !item.hasAttribute('data-pdp-poster') && !item.hasAttribute('data-pdp-detail-photo') && item.querySelector('.media-type-image'));
      const thumb = [...(thumbs?.children || [])].find(item => item.dataset.target === photo?.dataset.mediaId);
      const originalId = photo?.dataset.mediaId?.slice(this.dataset.sectionId.length + 1);
      const zoom = [...(modalList?.children || [])].find(item => item.dataset.mediaId === originalId);
      if (!photo || !thumb || !zoom) return; // Native photo gallery is the fallback.
      const id = poster ? 'mobilegs-presentation-still' : 'mobilegs-detail-' + key;
      const mediaId = this.dataset.sectionId + '-' + id;
      const clone = node => {
        const copy = node.cloneNode(true);
        copy.querySelectorAll('noscript').forEach(node => node.remove());
        const ids = new Map();
        [copy, ...copy.querySelectorAll('[id]')].forEach(node => {
          if (node.id) { ids.set(node.id, node.id + '-' + key); node.id += '-' + key; }
        });
        copy.querySelectorAll('[aria-describedby],[aria-labelledby],[for]').forEach(node => {
          ['aria-describedby', 'aria-labelledby', 'for'].forEach(attr => {
            if (node.hasAttribute(attr)) node.setAttribute(attr, node.getAttribute(attr).split(' ').map(value => ids.get(value) || value).join(' '));
          });
        });
        if (poster) copy.dataset.pdpPoster = '';
        else copy.dataset.pdpDetailPhoto = key;
        return copy;
      };
      const serverSlide = poster && list.querySelector('[data-pdp-server-poster]');
      const serverThumb = poster && thumbs.querySelector('[data-pdp-server-poster]');
      const serverZoom = poster && modalList.querySelector('[data-pdp-server-poster]');
      const serverRendered = serverSlide && serverThumb && serverZoom;
      const slide = serverRendered ? serverSlide : clone(photo), thumbnail = serverRendered ? serverThumb : clone(thumb), enlarged = serverRendered ? serverZoom : clone(zoom);
      const initialScrollBehavior = list.style.scrollBehavior;
      if (poster && !serverRendered) { this.presentationPending = true; list.style.scrollBehavior = 'auto'; }
      slide.id = 'Slide-' + mediaId;
      slide.dataset.mediaId = mediaId;
      slide.classList.remove('product__media-item--variant');
      if (!serverRendered) slide.classList.remove('is-active');
      const zoomButton = slide.querySelector('.product__media-toggle');
      zoomButton.dataset.mediaId = id;
      this.listen(zoomButton, 'click', event => {
        event.preventDefault(); event.stopImmediatePropagation(); modal.show(zoomButton);
      }, true);
      thumbnail.dataset.target = mediaId;
      thumbnail.classList.remove('thumbnail-list_item--variant');
      if (!serverRendered) thumbnail.querySelector('button').removeAttribute('aria-current');
      if (!serverRendered) this.listen(thumbnail.querySelector('button'), 'click', () => this.gallery.setActiveMedia(mediaId, false));
      enlarged.dataset.mediaId = id;
      enlarged.classList.remove('product__media-item--variant', 'active');
      if (poster) { if (!serverRendered) { list.prepend(slide); thumbs.prepend(thumbnail); modalList.prepend(enlarged); } }
      else {
        const images = [...slide.querySelectorAll('.product__media img'), thumbnail.querySelector('img'), enlarged];
        images.forEach(image => {
          image.removeAttribute('srcset');
          image.src = src;
          image.alt = alt;
          image.loading = 'lazy';
          image.style.objectFit = 'contain';
        });
        thumbnail.querySelector('button').setAttribute('aria-label', 'View ' + alt);
        zoomButton.setAttribute('aria-label', 'Enlarge ' + alt);
        list.append(slide); thumbs.append(thumbnail); modalList.append(enlarged);
      }
      [...thumbs.children].forEach((node, index) => {
        node.dataset.mediaPosition = String(index + 1);
      });
      if (poster) {
        this.posterSlide = slide;
        this.posterImages = [...slide.querySelectorAll('.product__media img'), thumbnail.querySelector('img'), enlarged];
      }
      this.cleanups.push(() => { if (!serverRendered) { slide.remove(); thumbnail.remove(); enlarged.remove(); } if (poster) this.posterSlide = null; });
      requestAnimationFrame(() => {
        if (this.dead) return;
        this.gallery.elements.viewer.resetPages();
        this.gallery.elements.thumbnails?.resetPages();
        if (poster && (this.previewDesign || this.selected)?.poster) this.gallery.setActiveMedia(mediaId, false);
        // Prepending the still must not animate from the old native slide.
        // Restore normal arrow/swipe behavior after Dawn's queued scroll.
        if (poster) setTimeout(() => {
          list.style.scrollBehavior = initialScrollBehavior;
          this.presentationPending = false;
          this.scheduleMeasure();
        }, 0);
        this.scheduleMeasure();
      });
    }
    syncOriginalPhoto(mapping = this.selected) {
      if (!this.posterSlide || !mapping?.mediaId) return;
      const list = this.posterSlide.parentElement;
      const thumbs = this.gallery.querySelector('[id^="Slider-Thumbnails-"]');
      const modalList = document.getElementById('ProductModal-' + this.dataset.sectionId)?.querySelector('.product-media-modal__content');
      const mediaId = String(mapping.mediaId);
      const fullId = this.dataset.sectionId + '-' + mediaId;
      const selected = [
        [...list.children].find(node => node.dataset.mediaId === fullId),
        [...(thumbs?.children || [])].find(node => node.dataset.target === fullId),
        [...(modalList?.children || [])].find(node => node.dataset.mediaId === mediaId)
      ];
      if (selected.some(node => !node)) return; // Keep the native fallback if media is incomplete.
      if (!this.originalPhotoState) {
        const ids = new Set(Object.values(this.mappings).map(entry => String(entry.mediaId)));
        const parents = [list, thumbs, modalList];
        const orders = parents.map(parent => [parent, [...parent.children]]);
        this.originalPhotoState = parents.flatMap((parent, index) => [...parent.children].filter(node => {
          const id = index === 1 ? node.dataset.target : node.dataset.mediaId;
          return ids.has(index === 2 ? id : id?.slice(this.dataset.sectionId.length + 1));
        }).map(node => ({ node, index, classes: node.className, hidden: node.hidden, display: node.style.display })));
        this.cleanups.push(() => {
          this.originalPhotoState.forEach(({node, classes, hidden, display}) => {
            node.className = classes; node.hidden = hidden; node.style.display = display;
          });
          orders.forEach(([parent, nodes]) => nodes.filter(node => node.parentElement === parent).forEach(node => parent.append(node)));
          this.originalPhotoState = null;
        });
      }
      // The still owns slot one. Slot two follows Shopify's assigned image;
      // shared lifestyle/detail photos remain untouched, including their URLs.
      this.originalPhotoState.forEach(({node, index, display}) => {
        const visible = node === selected[index];
        node.classList.toggle(index === 1 ? 'thumbnail-list_item--variant' : 'product__media-item--variant', !visible);
        node.hidden = !visible;
        node.style.display = visible ? display : 'none';
      });
      [list, thumbs, modalList].forEach((parent, index) => {
        const poster = [...parent.children].find(node => node.hasAttribute('data-pdp-poster'));
        if (poster && poster.nextElementSibling !== selected[index]) poster.after(selected[index]);
      });
      [...thumbs.children].filter(node => !node.hidden).forEach((node, index) => { node.dataset.mediaPosition = String(index + 1); });
      this.gallery.elements.viewer.resetPages();
      this.gallery.elements.thumbnails?.resetPages();
    }
    updatePresentation() {
      if (this.selector) {
        this.presentationEntries.forEach(entry => entry.button.setAttribute('aria-pressed', String(String(entry.variant.id) === this.selectedId)));
        const entry = this.presentationEntries.find(entry => String(entry.variant.id) === this.selectedId);
        if (entry) {
          this.selector.querySelector('strong').textContent = entry.mapping.name;
          this.selector.querySelector('.mg-pdp-selector__selected > span').textContent =
            entry.label.dataset.stylePrice + ' · Sold as a pair · ' + (entry.variant.available ? 'In stock' : 'Sold out');
        }
      }
      if (this.designSelector) {
        this.designSelector.querySelectorAll('[data-design]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.design === this.previewDesign.slug)));
        this.designSelector.querySelector('strong').textContent = this.previewDesign.name;
        if (this.universalCommerce) {
          this.designSelector.querySelector('.mg-pdp-selector__selected > span').textContent =
            this.selected.price + ' · Sold as a pair · ' + (this.selected.available ? 'In stock' : 'Sold out');
          this.designSelector.querySelector('.mg-pdp-selector__notice').hidden = true;
        } else {
          this.designSelector.querySelector('.mg-pdp-selector__selected > span').textContent =
            (this.previewDesign.classic ? this.dataset.productPrice : this.dataset.designPrice) + ' · Sold as a pair';
          this.designSelector.querySelector('.mg-pdp-selector__notice').textContent = this.previewDesign.classic
            ? 'Designs are preview only until launch.' : 'Preview price. This design will be available to order at launch.';
          this.updateUniversalPurchase();
        }
      }
      const presentation = this.previewDesign || this.selected;
      if (this.posterSlide && presentation?.poster) {
        this.posterImages.forEach(image => {
          image.removeAttribute('srcset');
          image.src = presentation.poster;
          image.alt = presentation.name + (this.previewDesign ? ' Mobilegs Universal crutch' : ' crutch');
          image.draggable = false;
        });
      }
    }
    updateUniversalPurchase() {
      if (this.previewDesign.classic) { this.restoreUniversalPurchase(); return; }
      if (this.previewPurchase) return;
      const button = this.form.querySelector('[name="add"]');
      const label = button?.querySelector('span');
      const price = document.getElementById('price-' + this.dataset.sectionId);
      const prices = [...(price?.querySelectorAll('.price-item') || [])].map(node => ({ node, text: node.textContent }));
      if (!button || !label) return;
      this.previewPurchase = { button, label, disabled: button.disabled, text: label.textContent, prices };
      button.disabled = true; label.textContent = 'Design preview';
      prices.forEach(({ node }) => { node.textContent = this.dataset.designPriceWithCurrency; });
    }
    restoreUniversalPurchase() {
      if (!this.previewPurchase) return;
      const { button, label, disabled, text, prices } = this.previewPurchase;
      button.disabled = disabled; label.textContent = text;
      prices.forEach(({ node, text }) => { node.textContent = text; });
      this.previewPurchase = null;
    }
    listen(target, type, handler, options) {
      if (!target) return;
      target.addEventListener(type, handler, options);
      this.cleanups.push(() => target.removeEventListener(type, handler, options));
    }
    disconnectedCallback() { if (this.initialized && !this.isConnected) this.destroy(); }
    variantMediaId() { return this.posterSlide && (this.previewDesign || this.selected)?.poster ? this.posterSlide.dataset.mediaId : `${this.dataset.sectionId}-${this.selected?.mediaId}`; }
    validMapping() {
      return Boolean(this.selected && this.dataset.viewerModel && (this.previewDesign ? this.previewDesign.shortTexture && this.previewDesign.longTexture : this.selected.shortTexture && this.selected.longTexture));
    }
    option() {
      const design = this.previewDesign || this.selected;
      return { dataset: { classic: String(design.classic), shortTexture: design.shortTexture,
        longTexture: design.longTexture, styleName: design.name } };
    }
    syncVariant() {
      if (this.dead) return;
      const id = this.form.querySelector('[name="id"]')?.value;
      if (id === this.selectedId) { this.scheduleMeasure(); return; }
      this.selectedId = id;
      this.selected = this.mappings[id];
      if (this.universalCommerce) this.previewDesign = this.designs.find(design => String(design.variant.id) === id);
      this.syncOriginalPhoto();
      this.updatePresentation();
      this.dataset.selectedVariantId = id || '';
      this.slide = this.gallery.querySelector(`[data-media-id="${CSS.escape(this.variantMediaId())}"]`) || this.slide;
      this.scheduleMeasure();
      if (!this.validMapping()) {
        this.photos();
        this.status.textContent = '3D is unavailable for this design. Product photos and purchasing are still available.';
      } else if (this.wants3D) {
        if (this.controller) this.applySelection();
        // Initial activation reads selected again after every asynchronous step.
      } else {
        this.setBusy(false);
        this.status.textContent = '';
      }
    }
    syncHistory() {
      if (this.dead || !this.picker?.getVariantData) return;
      const id = new URL(location.href).searchParams.get('variant');
      if (!id || id === this.form.querySelector('[name="id"]')?.value) return;
      const variant = this.picker.getVariantData().find(item => String(item.id) === id);
      if (!variant) return;
      const fields = [...this.picker.querySelectorAll('fieldset')];
      fields.forEach((field, index) => field.querySelectorAll('input[type="radio"]').forEach(input => { input.checked = input.value === variant.options[index]; }));
      const input = fields[0]?.querySelector('input:checked');
      if (input) input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    snapGallery(slider) {
      if (this.dead || !slider || this.wants3D || this.classList.contains('is-viewer-fullscreen') || !matchMedia('(width < 750px)').matches) return;
      const slides = [...slider.children].filter(node => node.clientWidth > 0);
      if (slides.length < 2) return;
      const start = slides[0].offsetLeft;
      const positions = slides.map(node => Math.max(0, Math.min(node.offsetLeft - start, slider.scrollWidth - slider.clientWidth)));
      const target = positions.reduce((best, next) => Math.abs(next - slider.scrollLeft) < Math.abs(best - slider.scrollLeft) ? next : best);
      if (Math.abs(target - slider.scrollLeft) < 1) return;
      const behavior = slider.style.scrollBehavior;
      slider.style.scrollBehavior = 'auto';
      slider.scrollTo({ left: target, behavior: 'auto' });
      slider.style.scrollBehavior = behavior;
    }
    scheduleMeasure() {
      if (this.dead || this.measureFrame) return;
      this.measureFrame = requestAnimationFrame(() => { this.measureFrame = null; this.measure(); });
    }
    measure() {
      if (this.dead || this.presentationPending || this.classList.contains('is-viewer-fullscreen')) return;
      // At a breakpoint, the native gallery can hide the remembered mobile
      // slide without emitting another page-number change. Use its displayed
      // active item instead of retaining the previous frame's dimensions.
      const slide = this.slide?.isConnected && this.slide.getBoundingClientRect().width
        ? this.slide : this.gallery.querySelector('[data-media-id].is-active');
      const frame = slide?.querySelector('.product-media-container.media-type-image');
      if (!frame || !this.validMapping()) { this.hidden = true; return; }
      const r = frame.getBoundingClientRect(), g = this.gallery.getBoundingClientRect();
      if (!r.width || !r.height) return;
      // An invisible overlay on an offscreen/portrait photo can enlarge the
      // document while swiping. Only show the inactive cue on a settled still.
      if (!this.active && this.posterSlide) {
        const viewport = this.posterSlide.parentElement.getBoundingClientRect();
        if (slide !== this.posterSlide || (matchMedia('(width < 750px)').matches && (r.left < viewport.left - 1 || r.right > viewport.right + 1))) {
          this.hidden = true;
          return;
        }
      }
      this.frame = frame;
      this.style.left = `${r.left - g.left}px`;
      this.style.top = `${r.top - g.top}px`;
      this.style.width = `${r.width}px`;
      this.style.height = `${r.height}px`;
      this.style.borderRadius = getComputedStyle(frame).borderRadius;
      this.hidden = false;
      this._mobilegsCueController?.sync();
      if (this.active) this.coverPhoto(frame);
    }
    coverPhoto(frame) {
      if (frame === this.covered?.frame) return;
      this.uncoverPhoto();
      const opener = frame.querySelector('modal-opener');
      this.covered = { frame, opener, inert: opener?.inert };
      frame.classList.add('mg-pdp-photo-covered');
      if (opener) opener.inert = true;
    }
    uncoverPhoto() {
      if (!this.covered) return;
      this.covered.frame.classList.remove('mg-pdp-photo-covered');
      if (this.covered.opener) this.covered.opener.inert = this.covered.inert;
      this.covered = null;
    }
    async applySelection({ focusOnFailure = false } = {}) {
      const controller = this.controller, selected = this.selected, design = this.previewDesign, request = ++this.generation;
      if (!controller || !selected || !this.validMapping()) return;
      this.classList.add('is-pdp-swapping');
      this.loading.hidden = !this.active;
      try {
        const applied = await controller.selectDesign(this.option());
        if (this.dead || request !== this.generation || controller !== this.controller || !this.wants3D || applied === false) return;
        controller.reveal();
        this.active = true;
        this.dataset.renderedVariantId = String(selected.id);
        if (design) {
          this.dataset.renderedDesign = design.slug;
          controller.viewer.alt = `Mobilegs Universal — ${design.name}`;
        }
        this.classList.add('is-pdp-3d');
        this.classList.remove('is-pdp-swapping');
        this.activateButton.hidden = true;
        this.backButton.hidden = false;
        this.loading.hidden = true;
        this.setBusy(false);
        this.measure();
      } catch (error) {
        if (!this.dead && request === this.generation && controller === this.controller) this.fail(error, focusOnFailure);
      }
    }
    setBusy(busy) {
      this.activateButton.disabled = busy;
      this.activateButton.setAttribute('aria-busy', String(busy));
      this.activateLabel.textContent = busy ? 'Loading 3D…' : 'View in 3D';
    }
    activate(focusViewer = false) {
      if (this.dead || !this.validMapping() || this.activation) return;
      // Tabbing across offscreen photo controls can leave Dawn's smooth
      // carousel scroll in flight. Settle at this trigger's current frame
      // before starting 3D; otherwise its late slideChanged event cancels the
      // load. Subsequent genuine photo navigation still uses the normal exit.
      const photoViewer = this.gallery.elements?.viewer;
      const activationSlide = this.frame?.closest('[data-media-id]') || this.slide;
      if (photoViewer?.slider && activationSlide && photoViewer.sliderItemOffset > 0 &&
          photoViewer.slider.scrollWidth > photoViewer.slider.clientWidth + 1) {
        photoViewer.slider.scrollTo({ left: activationSlide.offsetLeft, behavior: 'instant' });
        photoViewer.update();
      }
      this.wants3D = true;
      this.setBusy(true);
      this.status.textContent = 'Loading interactive model';
      const request = ++this.generation;
      const activation = {};
      this.activation = activation;
      (async () => {
        try {
          if (!this.controller) {
            const module = await loadModule(this.dataset.viewerModule);
            if (this.dead || request !== this.generation || !this.wants3D) return;
            this.abort = new AbortController();
            const controller = await module.activateStyleViewer({ mode: 'pdp', root: this, stage: this.stage,
              viewerRegion: this.region, status: this.status, signal: this.abort.signal });
            if (this.dead || request !== this.generation || !this.wants3D) { controller.destroy(); return; }
            // The bundled renderer defaults to touch-action:none. Let vertical
            // gestures scroll the PDP while horizontal drags rotate the model.
            controller.viewer.setAttribute('touch-action', 'pan-y');
            this.controller = controller;
          }
          await this.applySelection({ focusOnFailure: focusViewer });
          if (focusViewer && this.active && !this.dead &&
              (document.activeElement === document.body || this.contains(document.activeElement))) this.controller?.focus();
        } catch (error) {
          if (!this.dead && request === this.generation && error.name !== 'AbortError') this.fail(error, focusViewer);
        } finally {
          if (this.activation === activation) this.activation = null;
        }
      })();
    }
    photos({ restoreVariant = false, focus = false } = {}) {
      if (this.dead) return;
      this.generation += 1;
      this.wants3D = false;
      this.activation = null;
      this.active = false;
      this.controller?.suspend();
      if (!this.controller) { this.abort?.abort(); this.abort = null; }
      this.classList.remove('is-pdp-3d', 'is-pdp-swapping');
      this.dataset.renderedVariantId = '';
      this.uncoverPhoto();
      this.activateButton.hidden = false;
      this.backButton.hidden = true;
      this.loading.hidden = true;
      this.setBusy(false);
      if (restoreVariant) {
        const mediaId = this.variantMediaId();
        if (this.gallery.querySelector(`[data-media-id="${CSS.escape(mediaId)}"]`)) this.gallery.setActiveMedia(mediaId, false);
      }
      this.status.textContent = 'Product photos';
      this.scheduleMeasure();
      if (focus) {
        if (this._mobilegsCueController) this._mobilegsCueController.focus();
        else this.activateButton.focus({ preventScroll: true });
      }
    }
    fail(error, focus = false) {
      // Do not pull focus away if the shopper moved to the style selector or
      // another page control while loading. A disabled trigger may leave body
      // focused; that keyboard-started failure should return to Retry.
      focus = focus && (document.activeElement === document.body || this.contains(document.activeElement));
      this.controller?.destroy();
      this.controller = null;
      this.abort?.abort();
      this.abort = null;
      this.photos({ focus });
      this.activateLabel.textContent = 'Retry 3D';
      this.status.textContent = error.code === 'WEBGL_UNAVAILABLE'
        ? '3D is unavailable on this device. Product photos and purchasing are still available.'
        : '3D could not load. Product photos are available; try View in 3D again.';
    }
    destroy() {
      if (this.dead) return;
      this.photos();
      this.dead = true;
      this.generation += 1;
      this.abort?.abort();
      this.controller?.destroy();
      this.controller = null;
      this.cleanups.forEach(cleanup => cleanup());
      this.resizeObserver?.disconnect();
      if (this.measureFrame) cancelAnimationFrame(this.measureFrame);
      this.gallery.classList.remove('mg-pdp-gallery');
      this.hidden = true;
      this.initialized = false;
    }
  }
  customElements.define('mobilegs-pdp-viewer', MobilegsPdpViewer);
})();
